import { useRef } from 'react';
import PropTypes from 'prop-types';

import { generateUUID } from 'utils/functions/general';
import { fileUploaderIsolatedFileTypeEnum } from 'modules/common/enums';
import commonStrings from 'modules/common/strings';

import { Btn } from './FileUploaderIsolated.style';
// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const SUPPORTED_MIME_TYPES_REGEX = /image/i;
const MIME_TYPES_NOT_TO_BE_PROCESSED = /pdf/i;
const MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION = /pdf|video/i;
const FILE_UPLOAD_COUNT_LIMIT = 10;
const FILE_UPLOAD_SIZE_LIMIT = 10; // 10 MB
const MAX_IMG_WIDTH = 800;
const MAX_IMG_HEIGHT = 800;
const IMG_QUALITY = 0.7;
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const FileUploaderIsolated = ({
  children,
  shouldAllowMultipleFiles,
  shouldAllowSingleFileType,
  supportedMimeTypesRegex,
  fileInputAcceptAttrRegex,
  mimeTypesThatForcesOneFileOnlyRegex,
  filesCountLimit,
  filesSizeLimitInMB,
  indexToPrepend,
  uploadedData,
  setUploadedData,
  setErrorMessage,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const uploadSizeLimitInBytes = filesSizeLimitInMB * 1024 * 1024;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const fileInput = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const getUploadedFileType = fileMimeType => {
    if (/image/.test(fileMimeType)) return fileUploaderIsolatedFileTypeEnum.image;
    if (/video/.test(fileMimeType)) return fileUploaderIsolatedFileTypeEnum.video;
    return fileUploaderIsolatedFileTypeEnum.pdf;
  };

  const createWindowUrlFromFile = file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);
    return fileUrl;
  };

  const createImageFromFile = fileUrl =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.src = fileUrl;

      image.onload = () => {
        resolve(image);
      };

      image.onerror = reject;
    });

  const createVideoFromFile = fileUrl =>
    new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = fileUrl;

      video.onloadedmetadata = () => {
        const { duration } = video;
        video.currentTime = duration / 2;
        video.onseeked = () => {
          resolve(video);
        };
      };

      video.onerror = reject;
    });

  const resizeAndCompressMedia = media => {
    const { width, height } = media;
    const compressionDimensions = {};
    let dimenParamToChange = '';
    let ratio = 0;

    if (width > height) {
      const maxWidth = Math.min(width, MAX_IMG_WIDTH);
      compressionDimensions.width = maxWidth;
      compressionDimensions.height = Math.round((height * maxWidth) / width);
      dimenParamToChange = 'height';
      ratio = Math.round((height / width) * 10) / 10;
    } else {
      const maxHeight = Math.min(height, MAX_IMG_HEIGHT);
      compressionDimensions.width = Math.round((width * maxHeight) / height);
      compressionDimensions.height = maxHeight;
      dimenParamToChange = 'width';
      ratio = Math.round((width / height) * 10) / 10;
    }

    const dimensions = {
      dimenParamToChange,
      ratio,
    };

    const canvas = document.createElement('canvas');
    canvas.width = compressionDimensions.width;
    canvas.height = compressionDimensions.height;
    const canvasCtx = canvas.getContext('2d');
    canvasCtx.drawImage(media, 0, 0, compressionDimensions.width, compressionDimensions.height);

    return {
      compressedMediaCanvas: canvas,
      dimensions,
    };
  };

  const captureVideoFrame = media => {
    const canvas = document.createElement('canvas');
    canvas.width = media.videoWidth;
    canvas.height = media.videoHeight;
    const canvasCtx = canvas.getContext('2d');
    canvasCtx.drawImage(media, 0, 0, media.videoWidth, media.videoHeight);

    return {
      compressedMediaCanvas: canvas,
    };
  };

  const createFileFromCanvas = (canvas, fileName, fileType) => {
    const mediaDataUrl = canvas.toDataURL(fileType, IMG_QUALITY);
    const mediaDataUrlArr = mediaDataUrl.split(',');
    const mediaMimeType = mediaDataUrlArr[0].match(/:(.*?);/)[1];
    const mediaBase64 = mediaDataUrlArr[1];
    const mediaDecodedBase64 = atob(mediaBase64);
    let mediaDecodedBase64Length = mediaDecodedBase64.length;
    const mediaBinaryDataArr = new Uint8Array(mediaDecodedBase64Length);
    // eslint-disable-next-line no-plusplus
    while (mediaDecodedBase64Length--) {
      mediaBinaryDataArr[mediaDecodedBase64Length] =
        mediaDecodedBase64.charCodeAt(mediaDecodedBase64Length);
    }
    const compressedMediaFile = new File([mediaBinaryDataArr], fileName, { type: mediaMimeType });

    return {
      compressedMediaFile,
      mediaDataUrl,
    };
  };

  const processFile = async file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);

    const isVideo = file.fileTypeId === fileUploaderIsolatedFileTypeEnum.video;

    let media = {};
    if (isVideo) media = await createVideoFromFile(fileUrl);
    else media = await createImageFromFile(fileUrl);

    let compressedMediaCanvas = media;
    let dimensions = {};
    if (file.shouldBypassCompression === false)
      ({ compressedMediaCanvas, dimensions } = resizeAndCompressMedia(media));
    if (isVideo) ({ compressedMediaCanvas } = captureVideoFrame(media));

    const targetFileName = (isVideo && `${file.fileName.split('.')[0]}.jpg`) || file.fileName;
    const targetFileType = (isVideo && 'image/jpeg') || file.fileType;

    const { compressedMediaFile, mediaDataUrl } = createFileFromCanvas(
      compressedMediaCanvas,
      targetFileName,
      targetFileType,
    );

    const processedFileObj = {
      ...file,
      dataUrl: mediaDataUrl,
      isProcessed: true,
      compressedFileData: compressedMediaFile,
      dimensions,
    };

    return processedFileObj;
  };

  const prepareUploadedFileForProcessing = async uploadedFiles => {
    let { totalCompressedFilesSize } = uploadedData;
    const uploadedFilesData = [...uploadedFiles];

    for (let i = 0; i < uploadedFilesData.length; i += 1) {
      const currentFile = uploadedFilesData[i];
      let processedFile = currentFile;

      if (currentFile.isProcessed === false && currentFile.shouldBeProcessed) {
        // eslint-disable-next-line no-await-in-loop
        processedFile = await processFile(currentFile);
        processedFile.windowUrl = createWindowUrlFromFile(processedFile);
        totalCompressedFilesSize += processedFile.compressedFileData.size;
      } else if (currentFile.isProcessed === false) {
        const fileDataUrl = createWindowUrlFromFile(currentFile);
        processedFile = {
          ...processedFile,
          windowUrl: fileDataUrl,
          isProcessed: true,
        };
        totalCompressedFilesSize += processedFile.compressedFileData.size;
      }

      uploadedFilesData[i] = processedFile;
    }

    setUploadedData({
      uploadedFiles: uploadedFilesData,
      totalCompressedFilesSize,
    });
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleFileInputChange = event => {
    const { totalCompressedFilesSize } = uploadedData;
    let uploadedFiles = [...uploadedData.uploadedFiles];
    const { files } = event.target;

    const typesThatShouldHaveOnlyOneUploadedFile = {};
    uploadedFiles.forEach(file => {
      if (mimeTypesThatForcesOneFileOnlyRegex?.test(file.fileType)) {
        typesThatShouldHaveOnlyOneUploadedFile[file.fileTypeId] = 1;
      }
    });

    const currentFilesCount = uploadedFiles.length;
    let count = 1;
    let uploadingErrMsg = '';

    const referenceFileType =
      (currentFilesCount && uploadedFiles[0].fileTypeId) || getUploadedFileType(files[0].type);
    let shouldHandleFiles = true;

    const filesKeys = Object.keys(files);

    for (let i = 0; i < filesKeys.length; i += 1) {
      const currentFile = files[i];
      const { name, type } = currentFile;
      const typeId = getUploadedFileType(type);

      if (referenceFileType !== typeId && shouldAllowSingleFileType) {
        shouldHandleFiles = false;
        uploadingErrMsg = commonStrings.fileUploaderIsolated.errorMessages.sameFileTypeUpload;
        break;
      }

      if (
        mimeTypesThatForcesOneFileOnlyRegex?.test(type) &&
        typesThatShouldHaveOnlyOneUploadedFile[typeId]
      ) {
        shouldHandleFiles = false;
        const typeArr = type.split('/');
        uploadingErrMsg = commonStrings.fileUploaderIsolated.errorMessages.oneFileTypeUpload(
          typeArr[0],
        );
        break;
      }

      const fileAlreadyExist = uploadedFiles.some(item => item.fileName === name);
      const isFileTypeSupported = supportedMimeTypesRegex.test(type);
      const totalUploadedFilesCountIncludingCurrentOne = currentFilesCount + count;
      const isUploadingCountLimitChecked =
        filesCountLimit - totalUploadedFilesCountIncludingCurrentOne >= 0;
      const isUploadingSizeLimitChecked = currentFile.size <= uploadSizeLimitInBytes;

      if (
        !fileAlreadyExist &&
        isFileTypeSupported &&
        (isUploadingCountLimitChecked || filesCountLimit === 1) &&
        isUploadingSizeLimitChecked
      ) {
        const shouldBeProcessed = MIME_TYPES_NOT_TO_BE_PROCESSED.test(type) === false;
        const shouldBypassCompression = MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION.test(type);

        const fileObj = {
          id: generateUUID(),
          fileTypeId: getUploadedFileType(type),
          fileData: currentFile,
          fileName: name,
          fileType: type,
          dataUrl: '',
          windowUrl: '',
          shouldBeProcessed,
          isProcessed: !shouldBeProcessed,
          shouldBypassCompression,
          compressedFileData: (shouldBypassCompression && currentFile) || null,
          dimensions: {},
        };

        if (filesCountLimit === 1) uploadedFiles = [fileObj];
        else uploadedFiles.push(fileObj);

        count += 1;
      } else {
        uploadingErrMsg =
          (!isFileTypeSupported &&
            commonStrings.fileUploaderIsolated.errorMessages.notSupportedTypeUpload) ||
          (!isUploadingCountLimitChecked &&
            filesCountLimit > 1 &&
            commonStrings.fileUploaderIsolated.errorMessages.fileUploadCountLimit(
              filesCountLimit,
            )) ||
          (fileAlreadyExist &&
            commonStrings.fileUploaderIsolated.errorMessages.fileAlreadyUploaded) ||
          (!isUploadingSizeLimitChecked &&
            commonStrings.fileUploaderIsolated.errorMessages.fileUploadSizeLimit(
              filesSizeLimitInMB,
            )) ||
          '';
      }
    }

    if (fileInput && fileInput.current) fileInput.current.value = '';

    setErrorMessage(uploadingErrMsg);

    if (shouldHandleFiles) {
      setUploadedData({
        uploadedFiles,
        totalCompressedFilesSize,
      });

      prepareUploadedFileForProcessing(uploadedFiles);
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################

  const fileInputAcceptAttr = fileInputAcceptAttrRegex || 'image/*';

  return (
    <>
      <Btn
        id={`file-uploader-btn-${indexToPrepend}`}
        type="file"
        accept={fileInputAcceptAttr}
        multiple={shouldAllowMultipleFiles}
        ref={fileInput}
        onChange={event => handleFileInputChange(event)}
      />
      {children}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

FileUploaderIsolated.propTypes = {
  children: PropTypes.node.isRequired,
  shouldAllowMultipleFiles: PropTypes.bool,
  shouldAllowSingleFileType: PropTypes.bool,
  supportedMimeTypesRegex: PropTypes.instanceOf(RegExp),
  fileInputAcceptAttrRegex: PropTypes.string.isRequired,
  mimeTypesThatForcesOneFileOnlyRegex: PropTypes.instanceOf(RegExp),
  filesCountLimit: PropTypes.number,
  filesSizeLimitInMB: PropTypes.number,
  indexToPrepend: PropTypes.number,
  uploadedData: PropTypes.shape({
    uploadedFiles: PropTypes.arrayOf(PropTypes.shape({})),
    totalCompressedFilesSize: PropTypes.number,
  }),
  setUploadedData: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
};

FileUploaderIsolated.defaultProps = {
  shouldAllowMultipleFiles: false,
  shouldAllowSingleFileType: false,
  supportedMimeTypesRegex: SUPPORTED_MIME_TYPES_REGEX,
  mimeTypesThatForcesOneFileOnlyRegex: null,
  filesCountLimit: FILE_UPLOAD_COUNT_LIMIT,
  filesSizeLimitInMB: FILE_UPLOAD_SIZE_LIMIT,
  indexToPrepend: 0,
  uploadedData: {},
};

export default FileUploaderIsolated;
